import { Search, X } from "lucide-react";
import type { FC, PropsWithChildren, ReactNode } from "react";
import { useEffect, useRef, useState } from "react";

import { m } from "@recobra/i18n/messages";
import { Breadcrumb, BreadcrumbItem, BreadcrumbList, BreadcrumbPage } from "@recobra/ui/breadcrumb";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "@recobra/ui/input-group";

import { useNavHeader } from "./useNavHeader";

const SEARCH_DEBOUNCE_MS = 300;

export const ListPageLayout: FC<PropsWithChildren> = ({ children }) => (
  <div className="flex min-h-0 flex-1 flex-col gap-4">{children}</div>
);

export const ListPageHeader: FC<{
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
}> = ({ title, description, actions }) => {
  const breadcrumb = useNavHeader(
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem>
          <BreadcrumbPage>{title}</BreadcrumbPage>
        </BreadcrumbItem>
      </BreadcrumbList>
    </Breadcrumb>,
  );

  if (!description && !actions) return breadcrumb;

  return (
    <>
      {breadcrumb}
      <div className="flex items-start gap-3">
        {description ? <p className="text-sm text-muted-foreground">{description}</p> : null}
        {actions ? <div className="ml-auto shrink-0">{actions}</div> : null}
      </div>
    </>
  );
};

export const ListPageFilterBar: FC<PropsWithChildren> = ({ children }) => (
  <div className="flex flex-wrap items-center gap-2">{children}</div>
);

export const ListPageSearchInput: FC<{
  placeholder?: string;
  onDebouncedChange: (value: string) => void;
  debounceMs?: number;
  defaultValue?: string;
}> = ({ placeholder, onDebouncedChange, debounceMs = SEARCH_DEBOUNCE_MS, defaultValue = "" }) => {
  const resolvedPlaceholder = placeholder ?? m.common_search();

  const [value, setValue] = useState(defaultValue);
  const onDebouncedChangeRef = useRef(onDebouncedChange);

  useEffect(() => {
    onDebouncedChangeRef.current = onDebouncedChange;
  });

  useEffect(() => {
    const t = window.setTimeout(() => {
      onDebouncedChangeRef.current(value.trim());
    }, debounceMs);

    return () => window.clearTimeout(t);
  }, [value, debounceMs]);

  return (
    <InputGroup className="w-52">
      <InputGroupAddon align="inline-start">
        <Search className="size-3.5 text-muted-foreground" />
      </InputGroupAddon>
      <InputGroupInput
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={resolvedPlaceholder}
      />
      {value ? (
        <InputGroupAddon align="inline-end">
          <InputGroupButton size="icon-xs" variant="ghost" onClick={() => setValue("")}>
            <X />
          </InputGroupButton>
        </InputGroupAddon>
      ) : null}
    </InputGroup>
  );
};
